import type { Tokens } from '@ovComponents/0-tokens/useTokens';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { sortFlagNotesNewestFirst } from '../utils/reconFlagNotes';
import type { ReconFlagRow } from '../utils/reconFlags.types';
import { formatDateTimeInEastern } from './reconFlagDrawerOverviewFormatting';

export type ReconFlagsDrawerNotesHistoryProps = {
  sys: Tokens['sys'];
  flag: ReconFlagRow;
  t: TFunction;
};

export const ReconFlagsDrawerNotesHistory = ({
  sys,
  flag,
  t,
}: ReconFlagsDrawerNotesHistoryProps) => {
  const history = useMemo(
    () => sortFlagNotesNewestFirst(flag.notes),
    [flag.notes],
  );

  if (history.length === 0) {
    return (
      <Typography
        variant='bodySmall'
        sx={{ color: sys.color.onSurfaceVariant }}
      >
        {t('reconFlags.drawer.notesHistoryEmpty')}
      </Typography>
    );
  }

  return (
    <Box display='flex' flexDirection='column' gap={1.5}>
      {history.map((entry) => {
        const authorLabel = entry.author?.trim();

        return (
          <Box
            key={`${entry.createdAt}|${entry.authorId}|${entry.text}`}
            sx={{
              border: `1px solid ${String(sys.color.outlineVariant)}`,
              borderRadius: 1,
              p: 1.5,
              backgroundColor: String(
                sys.color.surfaceContainer ?? sys.color.surface,
              ),
            }}
          >
            <Box
              display='flex'
              flexWrap='wrap'
              alignItems='baseline'
              gap={0.5}
              sx={{ mb: 0.75 }}
            >
              {authorLabel && (
                <>
                  <Typography variant='labelSmall' weight='bold'>
                    {authorLabel}
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    sx={{ color: sys.color.onSurfaceVariant }}
                    aria-hidden
                  >
                    ·
                  </Typography>
                </>
              )}
              <Typography
                variant='labelSmall'
                sx={{ color: sys.color.onSurfaceVariant }}
              >
                {formatDateTimeInEastern(entry.createdAt)}
              </Typography>
            </Box>
            <Typography variant='bodySmall' sx={{ whiteSpace: 'pre-wrap' }}>
              {entry.text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
